import { Injectable } from '@angular/core';
import { DeferredPromise } from '@cf-shared/shared-util';
import { PopoverController } from './popover-controller';
import { AlertPopover } from 'components/cs-alert/cs-alert';
import { LoggingService } from 'services/logging.service';
import { TranslatedString } from 'models/translated-string';
import { alertDedupeKey } from './alert-dedupe-key';

export enum AlertButtonRole {
  Primary = 'primary',
  Cancel = 'cancel'
}

export interface AlertSelectOption {
  label: TranslatedString;
  value: string;
}

export interface AlertInputOptions {
  type?: string;
  name?: string;
  placeholder?: TranslatedString;
  value?: string;
  label?: TranslatedString;
  description?: TranslatedString;
  checked?: boolean;
  disabled?: boolean;
  id?: string;
  handler?: Function;
  min?: number;
  max?: number;
  selectOptions?: AlertSelectOption[];
}

export interface AlertButton<returnType> {
  text?: TranslatedString;
  role?: AlertButtonRole;
  /** Optional badge text to display alongside the button (e.g., "Recommended") */
  badge?: TranslatedString;
  /** If plain value, this is directly returned from `showAlertAsync()` when the button is clicked.
   *
   * If a function, it is evaluated when the button is clicked and the return value is returned from `showAlertAsync()`.
   * If this function returns a promise, the promise will be resolved and _that_ value will be actually returned.
   *
   * The input argument `data`, present only if the Alert has some inputs, is a map of current values of all inputs,
   * keyed by the `name` field of each input.
   *
   * @See: `AlertController.showAlertAsync()` for more details on button behavior.
   */
  result?: returnType | ((data: any) => returnType) | ((data: any) => Promise<returnType>);
}

export interface AlertOptions<returnType> {
  title?: TranslatedString;
  subTitle?: TranslatedString;
  message?: TranslatedString;
  messageMarkdown?: TranslatedString;
  mode?: string;
  inputs?: AlertInputOptions[];
  buttons?: AlertButton<returnType>[];
  enableBackdropDismiss?: boolean;
  /**
   * Result returned when the dialog is dismissed without pressing one of its buttons:
   * the X button, a backdrop click, the browser Back button, or an external call to
   * `popoverController.dismiss()`. When it is NOT provided, all of those resolve to
   * `undefined` — a Cancel role button's `result` is never used as a fallback.
   * When it IS provided, no explicit Cancel role button is required in the buttons array.
   */
  implicitCancelResult?: returnType;
  /// If this promise wins the race in `showAlertAsync`, that call returns THAT
  /// promise's value. Despite the name it dismisses NOTHING, and it cannot answer
  /// early: `showAlertAsync` awaits the popover first, and that await only resolves
  /// once the alert has already been dismissed. By then the clicked button's result
  /// is normally a settled value, which wins the race. So this only ever takes effect
  /// when that button's `result` is an async function still pending — it overrides a
  /// slow button handler, not the alert. No caller uses it today.
  externalDismiss?: Promise<returnType>;
  trace?: boolean; // if we want this alert to be shown in the trace log
}

@Injectable()
export class AlertController {
  constructor(
    public popoverController: PopoverController,
    private loggingService: LoggingService
  ) { }

  /** Show an `Alert` **and wait until it is dismissed**. Handles the browser Back button.
   *
   * What it resolves to, per route out of the alert:
   * * A button was clicked → that button's `result`, or `undefined` when the button
   *   has no `result` field.
   * * The alert was dismissed WITHOUT a button being clicked — the X button, a click
   *   outside the alert, the browser Back button, or an external call to
   *   `popoverController.dismiss()` → `opts.implicitCancelResult`, which is
   *   `undefined` when the caller did not supply it.
   * * `opts.externalDismiss` won the race → that promise's own value. Only reachable
   *   after the alert has already been dismissed, and only when the clicked button's
   *   `result` is a promise that has not settled yet (see that field).
   *
   * **A button with `role: 'cancel'` gets NO special treatment on the dismiss route.**
   * Its `result` is never used as a fallback; only `implicitCancelResult` is. So if the
   * cancel case needs a specific value, set `implicitCancelResult` to it. A caller that
   * skips it and then tests for a truthy cancel value (`answer === 'cancel'`, or a
   * `switch` over its own result values) silently takes whatever branch is left over.
   *  */
  public async showAlertAsync<returnType>(opts: AlertOptions<returnType>): Promise<returnType> {
    console.assert(!opts.message || !opts.messageMarkdown, 'Cannot have both `message` and `messageMarkdown` in AlertOptions');

    // Validate button roles
    this.validateButtonRoles(opts);

    if (opts.trace) {
      console.log('Alert: presenting popover');
    }

    let result = await this.popoverController.present(AlertPopover,
      {
        options: opts,
      },
      {
        targetFromElement: document.body,
        targetToElement: document.body,
        animate: true,
        showBackdrop: true,
        disableBackdropDismiss: opts.enableBackdropDismiss !== undefined && !opts.enableBackdropDismiss,
        dedupeKey: alertDedupeKey(opts),
      }
    );
    if (opts.trace) {
      console.log('Alert: popover presented');
    }

    let externalDismiss = opts.externalDismiss !== undefined ? opts.externalDismiss : DeferredPromise.defer<returnType>();
    let selection = await Promise.race([result.resultData, externalDismiss]);
    if (opts.trace) {
      console.log(`Alert: popover dismissed with result ${JSON.stringify(selection)}`);
    }
    return selection;
  }

  private validateButtonRoles<returnType>(opts: AlertOptions<returnType>): void {
    if (!opts.buttons || opts.buttons.length === 0) {
      // No buttons is valid
      return;
    }

    let primaryCount = opts.buttons.filter(b => b.role === AlertButtonRole.Primary).length;
    let cancelCount = opts.buttons.filter(b => b.role === AlertButtonRole.Cancel).length;
    let fixed = false;

    if (opts.buttons.length === 1) {
      // Single button must be Primary
      if (primaryCount === 0) {
        // Fix: assign Primary to the single button (even if it currently has Cancel role)
        opts.buttons[0].role = AlertButtonRole.Primary;
        fixed = true;
        primaryCount = 1;
        if (cancelCount === 1) {
          cancelCount = 0;
        }
      }
    } else {
      // 2+ buttons - need exactly 1 Primary and 1 Cancel (unless implicitCancelResult is provided)
      const hasImplicitCancel = opts.implicitCancelResult !== undefined;

      // Fix missing Primary
      if (primaryCount === 0) {
        // Find first button without a role and assign Primary
        const buttonWithoutRole = opts.buttons.find(b => !b.role);
        if (buttonWithoutRole) {
          buttonWithoutRole.role = AlertButtonRole.Primary;
          fixed = true;
          primaryCount = 1;
        } else if (cancelCount > 1) {
          // If all buttons have Cancel role, convert first to Primary
          const firstCancel = opts.buttons.find(b => b.role === AlertButtonRole.Cancel);
          if (firstCancel) {
            firstCancel.role = AlertButtonRole.Primary;
            fixed = true;
            primaryCount = 1;
            cancelCount--;
          }
        }
      }

      // Fix missing Cancel (skip if implicitCancelResult handles cancel)
      if (cancelCount === 0 && primaryCount > 0 && !hasImplicitCancel) {
        // Find first button without a role (that isn't the one we just assigned Primary to)
        const buttonWithoutRole = opts.buttons.find(b => !b.role);
        if (buttonWithoutRole) {
          buttonWithoutRole.role = AlertButtonRole.Cancel;
          fixed = true;
          cancelCount = 1;
        } else if (primaryCount > 1) {
          // If multiple Primary buttons, convert the second one to Cancel
          let foundFirst = false;
          for (const button of opts.buttons) {
            if (button.role === AlertButtonRole.Primary) {
              if (foundFirst) {
                button.role = AlertButtonRole.Cancel;
                fixed = true;
                primaryCount--;
                cancelCount = 1;
                break;
              }
              foundFirst = true;
            }
          }
        }
      }
    }

    // Sort buttons: Primary first, Cancel last, others maintain relative order
    opts.buttons.sort((a, b) => {
      // Primary always comes first
      if (a.role === AlertButtonRole.Primary && b.role !== AlertButtonRole.Primary) return -1;
      if (b.role === AlertButtonRole.Primary && a.role !== AlertButtonRole.Primary) return 1;

      // Cancel always comes last
      if (a.role === AlertButtonRole.Cancel && b.role !== AlertButtonRole.Cancel) return 1;
      if (b.role === AlertButtonRole.Cancel && a.role !== AlertButtonRole.Cancel) return -1;

      // Otherwise maintain original order
      return 0;
    });

    // After fixing, validate the final state
    const finalPrimaryCount = opts.buttons.filter(b => b.role === AlertButtonRole.Primary).length;
    const finalCancelCount = opts.buttons.filter(b => b.role === AlertButtonRole.Cancel).length;

    let isValid = false;
    let errorMessage = '';

    const hasImplicitCancel = opts.implicitCancelResult !== undefined;

    if (opts.buttons.length === 1) {
      isValid = finalPrimaryCount === 1 && finalCancelCount === 0;
      if (!isValid) {
        errorMessage = `Single-button alert must have Primary role. Found: ${finalPrimaryCount} Primary, ${finalCancelCount} Cancel`;
      }
    } else {
      // Multi-button: need 1 Primary and either 1 Cancel button OR implicitCancelResult
      const cancelSatisfied = finalCancelCount === 1 || hasImplicitCancel;
      isValid = finalPrimaryCount === 1 && cancelSatisfied;
      if (!isValid) {
        errorMessage = `Multi-button alert must have exactly 1 Primary and 1 Cancel (or implicitCancelResult). Found: ${finalPrimaryCount} Primary, ${finalCancelCount} Cancel, hasImplicitCancel: ${hasImplicitCancel}`;
      }
    }

    // Log the issue (even if we fixed it)
    if (!isValid || fixed) {
      const context = {
        title: opts.title,
        message: opts.message || opts.messageMarkdown,
        buttonCount: opts.buttons.length,
        buttons: opts.buttons.map(b => ({
          text: b.text,
          role: b.role
        })),
        wasFixed: fixed,
        errorMessage: fixed ? `Auto-fixed: ${errorMessage}` : errorMessage,
        stack: new Error().stack,
      };

      this.loggingService.trackSilentProblem('AlertButtonRoles', context);
    }
  }
}
